// Central content source for the Aftab Azad executive brand site.
import {
  Compass, Users, Layers, Rocket, BrainCircuit, Database,
  Factory, Building2, Banknote, ShoppingBag, HeartPulse, Truck,
  TrendingUp, Sparkles, Gauge, Target, Handshake, Eye, ShieldCheck,
  Award, Scale, Globe,
  Workflow, Contact, UsersRound, Cloud, Cpu,
} from "lucide-react";

export const BRAND = {
  name: "Aftab Azad",
  role: "Executive Technology Advisor & Fractional CIO",
  tagline: "Trusted advisor to CEOs and boards on the technology decisions that shape enterprise value.",
  location: "Based in Mumbai • Available Globally • Remote & On-site",
  email: "CIO@aftabazad.com",
  socials: {
    linkedin: "https://www.linkedin.com/in/aftabazad/",
    x: "#",
    youtube: "#",
  },
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  {
    label: "Advisory",
    children: [
      { label: "Fractional CIO", to: "/services/fractional-cio" },
      { label: "Executive Advisory", to: "/services/executive-advisory" },
      { label: "Technology Strategy", to: "/services/technology-strategy" },
      { label: "Digital Transformation", to: "/services/digital-transformation" },
      { label: "AI Strategy", to: "/services/ai-strategy" },
      { label: "ERP & SAP Advisory", to: "/services/erp-sap-advisory" },
    ],
  },
  { label: "Industries", to: "/industries" },
  {
    label: "Insights",
    children: [
      { label: "Success Stories", to: "/success-stories" },
      { label: "Insights", to: "/insights" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

// Credibility pillars (no fabricated statistics).
export const PILLARS = [
  "27+ Years Executive Leadership",
  "Enterprise Digital Transformation",
  "Board & Executive Advisory",
  "Technology Strategy",
];

export const GEOGRAPHY = ["India", "UAE", "Singapore", "Middle East", "Global"];

export const LOCATIONS = ["India", "UAE", "Singapore", "Global"];

// Measurable executive outcomes (categories, not fabricated numbers).
export const OUTCOMES = [
  { title: "Business Growth", note: "Technology that opens new revenue, markets and capacity." },
  { title: "Operational Excellence", note: "A leaner, faster and more resilient operating core." },
  { title: "Investment Optimisation", note: "Every technology pound tied to a business return." },
  { title: "Executive Visibility", note: "A clear, board-level view of value, cost and risk." },
  { title: "Risk Reduction", note: "Cyber, delivery and vendor risk brought under control." },
  { title: "Faster Decisions", note: "The clarity to decide with confidence, and at pace." },
  { title: "Customer Experience", note: "Systems that make the business easier to buy from." },
  { title: "Competitive Advantage", note: "Capability that competitors cannot easily copy." },
];

export const TECH_STACK = [
  { name: "SAP S/4HANA", slug: "sap" },
  { name: "AI", icon: BrainCircuit },
  { name: "RPA", icon: Workflow },
  { name: "CRM", icon: Contact },
  { name: "ERP", icon: Database },
  { name: "HCM", icon: UsersRound },
  { name: "Cloud Computing", icon: Cloud },
  { name: "Cybersecurity", icon: ShieldCheck },
  { name: "IoT", icon: Cpu },
];

export const MANIFESTO = [
  { no: "01", title: "Lead with the business", body: "I start with the outcomes the board cares about, growth, margin and risk, and work back to the technology that serves them." },
  { no: "02", title: "Clarity over complexity", body: "Leaders need clear choices, not more information. I reduce the noise to a small number of decisions you can defend." },
  { no: "03", title: "Judgement over frameworks", body: "Experience matters most when the stakes are high and the path is unclear. That is the work leaders trust me with." },
  { no: "04", title: "Accountable, always", body: "Advice is only worth giving if you are willing to stand behind the result. I stay through to the outcome." },
];

export const CHALLENGES = [
  { icon: TrendingUp, q: "Is our technology spend actually creating value?", a: "Investment keeps rising while the link to revenue, margin and growth stays unclear. I make that connection explicit, and defensible to the board." },
  { icon: Database, q: "Is our core platform ready for the next decade?", a: "ERP and legacy systems were built for a business you have outgrown. I lead the modernisation decision so the platform sets the pace for growth, not the ceiling on it." },
  { icon: Sparkles, q: "Where does AI create real value, and where is it noise?", a: "The question is not whether to act, but where. I separate the few uses that move the business from the many that will not, and put the guardrails in place to adopt them safely." },
  { icon: ShieldCheck, q: "How exposed are we to technology and cyber risk?", a: "Risk now sits on the board's agenda, not the IT department's. I give leadership a clear view of the exposure that matters and the governance to manage it with confidence." },
  { icon: Gauge, q: "Can our systems scale with our ambition?", a: "Growth exposes what was never built to carry it. I modernise the operating core so the business can scale, enter new markets and move at the speed the opportunity demands." },
  { icon: Target, q: "Who owns technology as a business decision?", a: "Too often technology is managed as a cost, not governed as value. I bring a single, accountable, board-level voice to the decisions that shape enterprise value." },
];

export const WHY = [
  { icon: Target, title: "Business-first thinking", body: "Every recommendation is judged on business value: growth, margin and risk. Technology is the means, never the point." },
  { icon: Award, title: "27+ years of leadership", body: "Decisions made in real boardrooms, on real programmes, with real capital at stake, not theory." },
  { icon: Eye, title: "A board-level perspective", body: "I speak the language of the board and translate technology into the terms that drive their decisions." },
  { icon: Scale, title: "Independent advisory", body: "No products to sell and no platforms to push. My only interest is the decision that is right for you." },
  { icon: Handshake, title: "Vendor-neutral recommendations", body: "Objective evaluation of platforms and partners, free of commercial bias or hidden incentives." },
  { icon: Globe, title: "Global experience", body: "Leadership across India, the UAE, Singapore and the Middle East, at home in complex, multi-market organisations." },
  { icon: Layers, title: "Enterprise transformation expertise", body: "Deep experience leading large, high-stakes change from the business case through to the result." },
  { icon: Compass, title: "A long-term partnership", body: "I stay from the decision through delivery, accountable to the outcome, not just the advice." },
];

export const ASSESSMENT = {
  title: "The Executive Technology Assessment",
  intro: "A confidential, board-ready view of where your technology stands against your growth ambition, and the two or three decisions that would matter most.",
  points: [
    "A clear read on value, risk and capability",
    "The priorities that would move the business",
    "An independent, jargon-free perspective",
    "No cost, no obligation, no sales pitch",
  ],
};

export const SERVICES = [
  {
    slug: "fractional-cio",
    icon: Compass,
    title: "Fractional CIO",
    lead: "Senior technology leadership in your executive team, without a full-time appointment.",
    summary:
      "Many businesses carry significant technology cost and risk without a single leader accountable for the return. As your Fractional CIO, I own the technology agenda at board level, setting direction, controlling investment, and holding delivery to commercial outcomes.",
    outcomes: [
      "A single leader accountable for technology to the board",
      "Technology investment tied directly to business performance",
      "Confident governance of spend, risk and vendors",
    ],
    approach: [
      { title: "Assess", body: "A candid review of technology cost, risk and capability, and how well it serves the business." },
      { title: "Align", body: "A prioritised agenda agreed with the board and mapped to commercial goals." },
      { title: "Lead", body: "Hands-on leadership of delivery, teams and vendors, at a cadence that suits you." },
    ],
  },
  {
    slug: "executive-advisory",
    icon: Users,
    title: "Executive Advisory",
    lead: "An independent voice in the room when the decision matters most.",
    summary:
      "The decisions with the greatest consequence rarely have clear answers. I advise CEOs, boards and investors on major technology, investment and transformation choices, bringing judgement, candour and a clear recommendation.",
    outcomes: [
      "Independent validation of high-stakes decisions",
      "Clearer trade-offs and materially lower decision risk",
      "A trusted counsel for the CEO and board",
    ],
    approach: [
      { title: "Understand", body: "The commercial thesis, the pressures and the constraints, from the leadership's perspective." },
      { title: "Advise", body: "A direct recommendation, with the risks and trade-offs made explicit." },
      { title: "Stand by", body: "Continued counsel through negotiations, decisions and critical milestones." },
    ],
  },
  {
    slug: "technology-strategy",
    icon: Layers,
    title: "Technology Strategy",
    lead: "A technology direction the board can back and the business can execute.",
    summary:
      "Technology investment often drifts from where the business actually competes. I build a clear, commercially-grounded strategy, connecting capability and investment to growth, margin and resilience.",
    outcomes: [
      "A clear, board-ready technology direction",
      "Investment focused on genuine competitive advantage",
      "A practical, sequenced plan, not shelfware",
    ],
    approach: [
      { title: "Diagnose", body: "Benchmark current capability against your ambition and your market." },
      { title: "Define", body: "Set the target direction, operating model and order of priorities." },
      { title: "Decide", body: "Focus investment on the moves with the highest business return." },
    ],
  },
  {
    slug: "digital-transformation",
    icon: Rocket,
    title: "Digital Transformation",
    lead: "Change that reaches the business, not just the roadmap.",
    summary:
      "Transformation programmes too often deliver activity without impact. I lead change from the business case to the result, governing pace, adoption and accountability so the gains are real and they endure.",
    outcomes: [
      "Leaner, faster and more resilient core operations",
      "Measurable gains in customer and commercial performance",
      "An organisation that adopts change and sustains it",
    ],
    approach: [
      { title: "Frame", body: "Define the transformation thesis and the value genuinely at stake." },
      { title: "Mobilise", body: "Stand up the governance, teams and partners to move at pace." },
      { title: "Embed", body: "Build the capability so the gains hold long after go-live." },
    ],
  },
  {
    slug: "ai-strategy",
    icon: BrainCircuit,
    title: "AI Strategy",
    lead: "A clear, commercial position on AI, where it pays, and where it does not.",
    summary:
      "AI creates as much noise as opportunity. I help leadership separate genuine value from hype, prove it quickly, and adopt it responsibly, with the governance to scale without exposing the business.",
    outcomes: [
      "A focused portfolio of high-value AI opportunities",
      "Responsible, governed and secure adoption",
      "The confidence to move from pilot to production",
    ],
    approach: [
      { title: "Identify", body: "Surface the uses with a clear return and realistic feasibility." },
      { title: "Prove", body: "Validate value quickly through tightly-scoped pilots." },
      { title: "Govern", body: "Put the data, guardrails and operating model in place to scale safely." },
    ],
  },
  {
    slug: "erp-sap-advisory",
    icon: Database,
    title: "ERP & SAP Advisory",
    lead: "The platform decision and the delivery, made with confidence.",
    summary:
      "Core platform programmes are among the largest and riskiest investments a business makes. I provide independent advice on the decision, the partner and the delivery, protecting both the investment and the value it is meant to create.",
    outcomes: [
      "The right platform decision, made with confidence",
      "Programmes kept honest, on scope and on plan",
      "Business value realised, not just software deployed",
    ],
    approach: [
      { title: "Select", body: "Independent evaluation of platforms and implementation partners." },
      { title: "Assure", body: "Oversight that keeps a complex, costly delivery on track." },
      { title: "Realise", body: "Drive the adoption and benefits the business signed up for." },
    ],
  },
];

export const INDUSTRIES = [
  { icon: Factory, name: "Manufacturing", body: "Modernising operations and supply chains to protect margin and build resilience." },
  { icon: Building2, name: "Enterprise & Global", body: "Aligning technology decisions across complex, multi-region organisations." },
  { icon: Banknote, name: "Private Equity", body: "Supporting value creation and diligence across the investment lifecycle." },
  { icon: ShoppingBag, name: "Retail & Consumer", body: "Connecting commerce, data and customer experience to commercial growth." },
  { icon: HeartPulse, name: "Healthcare", body: "Modernising care and operational systems securely and responsibly." },
  { icon: Truck, name: "Logistics & Distribution", body: "Building visibility and efficiency across the network." },
];

// Executive problem-solving frameworks (no invented clients or numbers).
export const FRAMEWORKS = [
  {
    slug: "first-100-days",
    area: "Value Creation",
    title: "The First 100 Days",
    body: "A structured approach to establishing technology leadership at pace, assessing value and risk, aligning the board, and setting the two or three priorities that matter most in the first quarter.",
    steps: ["Assess value, risk and capability", "Align the board on priorities", "Mobilise the highest-value moves"],
  },
  {
    slug: "technology-due-diligence",
    area: "Investment & M&A",
    title: "Technology Due Diligence",
    body: "An independent framework for evaluating technology risk and opportunity ahead of an investment, surfacing what sits beneath the surface, and re-basing the thesis where the evidence demands it.",
    steps: ["Test the technology thesis", "Surface hidden risk and cost", "Shape the 100-day value plan"],
  },
  {
    slug: "transformation-recovery",
    area: "Programme Recovery",
    title: "Getting Transformation Back on Track",
    body: "A disciplined method for stabilising a stalled or at-risk programme, resetting governance, scope and plan, and restoring the board's confidence in delivery.",
    steps: ["Stabilise governance and scope", "Reset the plan and the team", "Restore delivery confidence"],
  },
];

export const INSIGHTS = [
  {
    slug: "technology-as-a-board-agenda",
    category: "Board Governance",
    title: "Why technology now belongs on every board agenda",
    excerpt: "Technology has become a primary driver of enterprise value and risk. Here is how the best boards govern it as a strategic responsibility, not a line item.",
    read: "6 min read",
  },
  {
    slug: "ai-without-the-hype",
    category: "AI",
    title: "AI without the hype: a leader's guide to real value",
    excerpt: "A practical way to separate durable AI value from noise, and to build a portfolio your CFO will actually approve.",
    read: "8 min read",
  },
  {
    slug: "modernising-the-core",
    category: "ERP Strategy",
    title: "Modernising the core without betting the business",
    excerpt: "ERP and platform programmes are the largest, riskiest investments most enterprises make. Here is how leaders get the decision and the delivery right.",
    read: "7 min read",
  },
  {
    slug: "decisions-that-last",
    category: "Executive Decision-Making",
    title: "Making technology decisions that last a decade",
    excerpt: "The platform choice is a fraction of the outcome. This is where the rest of the enterprise value is won or lost.",
    read: "6 min read",
  },
  {
    slug: "governing-cyber-risk",
    category: "Cybersecurity",
    title: "Governing technology and cyber risk at board level",
    excerpt: "What non-executive directors should be asking about cyber and technology risk, and what good answers actually sound like.",
    read: "5 min read",
  },
  {
    slug: "fractional-leadership",
    category: "Technology Leadership",
    title: "The rise of fractional executive leadership",
    excerpt: "How ambitious mid-market and PE-backed businesses are accessing board-level technology leadership without full-time cost.",
    read: "5 min read",
  },
];

// Speaking & perspective topics (not published-video claims).
export const VIDEOS = [
  { id: "board-tech", title: "Technology as a driver of enterprise value", topic: "Board & Executive Advisory", desc: "How the best boards govern technology as a strategic asset, and hold it to the same standard as capital." },
  { id: "ai-roi", title: "Building an AI agenda with real business return", topic: "AI Strategy", desc: "Separating durable AI value from noise, and building a portfolio a CFO will actually fund." },
  { id: "digital-ops", title: "Transformation that reaches the business", topic: "Digital Transformation", desc: "Why most programmes deliver activity without impact, and what disciplined leadership changes." },
  { id: "erp-truths", title: "Getting large platform programmes right", topic: "ERP & SAP", desc: "The decisions that make or break the largest, riskiest investment most enterprises will ever make." },
];

// Executive briefings, available on request (no fabricated downloads).
export const RESOURCES = [
  { title: "The Board's Guide to Technology Value", type: "Executive Briefing", desc: "A concise framework for governing technology as a strategic asset." },
  { title: "An AI Readiness Perspective", type: "Executive Briefing", desc: "How to assess your organisation across data, talent, governance and use cases." },
  { title: "The Platform Decision Framework", type: "Executive Briefing", desc: "A structured, unbiased way to evaluate core platforms and partners." },
  { title: "The First 100 Days of a Fractional CIO", type: "Executive Briefing", desc: "What board-level technology leadership delivers in the first quarter." },
];
